import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "app/envConfig";
import { loadStateWithKey } from "common/redux/redux-utils";
import { camelToSnakeCase, deepRename, populateKey, snakeToCamelCase } from "common/utils";
import { v4 as uuidv4 } from "uuid";

// Keys whose children should not be renamed (custom field slugs must be preserved as-is).
// Each pair is [camelCase, snake_case] so both request and response directions are covered.
const CUSTOM_FIELD_KEYS = [
  ["customFields", "custom_fields"],
  ["customFieldValues", "custom_field_values"],
].flat();

const API_REDUCER_KEY = "api";

const AUTH_STORAGE_KEY = config.useAuth0 ? "rtk:auth0" : "rtk:auth";

function getBaseQuery() {
  return fetchBaseQuery({
    baseUrl: config.apiBaseUrl,
    prepareHeaders: (headers, api) => {
      // @ts-ignore
      const state = loadStateWithKey(AUTH_STORAGE_KEY);

      // If we have a token set in state, let's assume that we should be passing it.
      if (state?.accessToken) {
        headers.set("Authorization", `Bearer ${state.accessToken}`);
      } else {
        return null;
      }

      // Generate a UUID for each request for tracking purposes.
      headers.set("x-request-id", uuidv4());

      return headers;
    },
  });

  // NOTE: We can add retry logic here if we want to. We initially put this in bc our AppRunners were not
  // handling enough traffic, and the backend was returning 429s (see https://github.com/Aktos-Inc/react-redux-dashboard/pull/2039).
  //
  // We've disabled it for now, bc the response times are now sub-second.
  // If we need to add it back, we can do so by uncommenting the code below.

  // return retry(baseQuery, {
  //   retryCondition: (error, args, { attempt }) => {
  //     return (
  //       attempt < 2 &&
  //       (error?.status === "FETCH_ERROR" || error?.status >= 500 || error?.status === 429)
  //     );
  //   },
  //   backoff: async (attempt) => {
  //     // waits for an exponential backoff time before retrying
  //     // 1000ms, 2000ms, 4000ms, 8000ms, 10000ms
  //     const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  //     const backoffTime = Math.min(2 ** attempt * 1000, 10000); // max 10s
  //     await sleep(backoffTime);
  //   },
  // });
}

export function transformResponse(response, customGetter) {
  return populateKey(
    deepRename(response.results ?? response.result, snakeToCamelCase, CUSTOM_FIELD_KEYS),
    customGetter,
  );
}

export function transformCursorResponse(response, customGetter) {
  const { results, result, ...meta } = response;
  return {
    meta: deepRename(meta, snakeToCamelCase, CUSTOM_FIELD_KEYS),
    result: deepRename(result, snakeToCamelCase, CUSTOM_FIELD_KEYS),
    results: populateKey(deepRename(results, snakeToCamelCase, CUSTOM_FIELD_KEYS), customGetter),
  };
}

export function transformRequest(request) {
  return deepRename(request, camelToSnakeCase, CUSTOM_FIELD_KEYS);
}

/**
 * Recursively replaces empty arrays with null in a deeply nested object.
 *
 * @param {Object} obj - The object to process.
 * @returns {Object} - The modified object with empty arrays replaced by null.
 */
export function replaceEmptyArraysWithNulls(obj) {
  if (Array.isArray(obj) && obj.length === 0) {
    return null;
  }

  if (typeof obj === "object" && obj !== null) {
    Object.keys(obj).forEach((key) => {
      obj[key] = replaceEmptyArraysWithNulls(obj[key]);
    });
  }

  return obj;
}

export function transformMultipart(values, file) {
  const formData = new FormData();

  Object.keys(values).forEach((key) => {
    const itemKey = camelToSnakeCase(key);

    // For arrays, we need to append each value individually with the same key, e.g
    // formData.append("tags", tag1)
    // formData.append("tags", tag2)
    // => { tags: [tag1, tag2] } on the back-end.
    if (Array.isArray(values[key])) {
      values[key].forEach((eachValue) => {
        formData.append(itemKey, eachValue);
      });
    } else {
      formData.append(itemKey, values[key]);
    }
  });

  if (file) {
    if (Array.isArray(file) && file.length > 0) {
      // Currently the back-end only supports 1 file upload.  We may need to support more
      // than that eventually.
      formData.append("file", file[0].originFileObj || file[0]);
    } else {
      formData.append("file", file.originFileObj || file);
    }
  }

  return formData;
}

const api = createApi({
  reducerPath: API_REDUCER_KEY,
  baseQuery: getBaseQuery(),
  endpoints: () => ({}),
  // 60 seconds TTL cache for the same query/data
  refetchOnMountOrArgChange: 60,
});

export default api;
